import { Accordion } from '@outdoorsyco/bonfire';
import React from 'react';

import { FAQsStructuredData } from './FAQsStructuredData';
import { TFAQItem } from './types';

type TFAQsProps = {
  items?: TFAQItem[];
  rvType?: string;
};

export const FAQs = ({ items, rvType = 'RV' }: TFAQsProps) => {
  const defaults: TFAQItem[] = [
    {
      question: `What are ${rvType} rentals?`,
      answer: `Renting an ${rvType} lets you enjoy the flexibility of ${rvType} travel without all the costs and maintenance of ownership. With a rental, you can book the perfect rig for any trip just like you would a car or hotel. With Outdoorsy, you rent directly from the host but get the backing of programs like roadside assistance, insurance, and stellar support from real, live people 24/7.`,
    },
    {
      question: `How much are average ${rvType} rentals?`,
      answer: `On average, you can expect to pay between $75 and $150 per night to rent most small trailers and campervans. Larger trailers and motorhomes could cost $100 to $250 per night. Renting an ${rvType} for a longer time can be even more affordable–a week or month-long rental could average out to less than $60 per day.`,
    },
    {
      question: `Are there ${rvType} rentals with unlimited mileage?`,
      answer: `Yes! Each ${rvType} host can choose whether to offer unlimited miles on their vehicles. You’ll be able to see if unlimited mileage is an option on any listing you check out. If unlimited miles are not available, you’ll be able to see what is included with the base rate and what extra miles will cost.`,
    },
    {
      question: `Are ${rvType} rentals pet-friendly?`,
      answer: `Many ${rvType} hosts allow pets in their rentals. Be sure to check the listing to see if it’s an option for the rig you have your eye on. You’ll be able to see if there is an extra deposit or fee for pets before you request a booking.`,
    },
    {
      question: `Do ${rvType} rentals offer delivery?`,
      answer: `Having an ${rvType} delivered to your home or destination is a great way to simplify your trip. Many ${rvType} hosts offer delivery and setup services, usually for an extra fee or within a certain distance of their location. Each listing shows whether or not delivery is an option.`,
    },
    {
      question: `Are there ${rvType} rentals near me?`,
      answer: `${rvType} rentals are available all over the country! Whether you want to pick up an ${rvType} near home to set out an epic road trip or if you want to have one ready at your destination, you can search by location for any type of rig.`,
    },
    {
      question: `${rvType} rental vs buying?`,
      answer: `Renting an ${rvType} is an affordable way to get all the benefits of ${rvType} travel without the maintenance and overhead of ownership. It’s also a great way to try out the lifestyle and different types of rigs before you take the plunge and buy your own. Already own an ${rvType}? Consider renting yours out to bring in some extra income.`,
    },
    {
      question: `How many people can sleep in an ${rvType}?`,
      answer:
        'Small travel trailers and campervans can usually sleep 2-4 people comfortably. Mid-size trailers and motorhomes usually sleep 4-8. Bigger rigs often sleep 6-10, some even 12!',
    },
    {
      question: 'Why choose Outdoorsy vs Craigslist or Airbnb?',
      answer: `Security, for one. Outdoorsy helps protect ${rvType} hosts and renters with excellent support and insurance options, and a secure platform for listings, communication, and payments. There are plenty of other reasons though, like roadside assistance, detailed listings, and a wide variety of ${rvType}s to choose from.`,
    },
  ];

  // Keep using the static FAQs if no items are provided
  const faqs = items?.length ? items : defaults;

  return (
    <>
      <FAQsStructuredData items={faqs} />

      <Accordion allowMultipleOpen>
        {faqs.map(faq => (
          <Accordion.Item key={faq.question} title={faq.question} divider>
            <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
          </Accordion.Item>
        ))}
      </Accordion>
    </>
  );
};
